import { useNavigate } from "react-router-dom";
import axios, { AxiosResponse } from "axios";
import { API_BASE_URL } from "@libs/apiClient";
import { TOKEN_REFRESH_URL } from "@libs/apiUrl";
import { queryClient } from "@libs/reactQuery/queryClient";
import { extractErrorCode } from "./extractErrorCode";

class CustomError_Class extends Error {
  response?: {
    data: any;
    status: number;
    headers: string;
  };
}

const useAxios = () => {
  const navigate = useNavigate();

  const clearUser = () => {
    localStorage.removeItem("isLoggedIn");
    localStorage.removeItem("isAutoLogin");
    localStorage.removeItem("order-storage");
    localStorage.removeItem("accessToken");

    queryClient.setQueryData(["userInfo"], "");
    navigate("/");
  };

  const refreshAccessToken = async () => {
    try {
      const res = await axios.post(
        TOKEN_REFRESH_URL,
        null,
        {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken") || ""}`,
          },
        },
      );

      if (res.status === 200) {
        const newToken = res.data?.data?.access_token;
        if (newToken) {
          localStorage.setItem("accessToken", newToken);
        }
        return true;
      } else {
        clearUser();
        throw new Error("Failed to refresh token");
      }
    } catch (error) {
      clearUser();
      throw new Error("Error refreshing access token");
    }
  };

  const axiosInstance = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
  });

  axiosInstance.interceptors.request.use(
    async (req: any) => {
      const token = localStorage.getItem("accessToken");
      if (token) {
        req.headers.Authorization = `Bearer ${token}`;
      }
      return req;
    },

    (error: unknown) => {
      if (error instanceof CustomError_Class) {
        console.log("error :", error);
      }
    },
  );

  axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error) => {
      const originalRequest = error.config;

      if (
        extractErrorCode(error) === "1002" &&
        !originalRequest._retry
      ) {
        originalRequest._retry = true;
        try {
          await refreshAccessToken();
          return axiosInstance(originalRequest);
        } catch (err) {
          clearUser();
          return Promise.reject(err);
        }
      }

      // 401 인증 실패 (토큰 만료가 아닌 경우) → 세션 정리 후 로그인으로 이동
      if (error?.response?.status === 401) {
        clearUser();
      }

      return Promise.reject(error);
    },
  );

  return { axiosInstance };
};

export default useAxios;
